import { z } from "zod";
import type { RoleAssignmentItem } from "./scriptRoleAssignment";
import type { ScriptRoleMatchMemberSnapshot } from "./scriptRoleAssignmentCache";

export const SCRIPT_ROLE_REVIEW_SCHEMA_VERSION = "2026-07-11-v2";

export const scriptRoleReviewSelectionSchema = z.object({
  tempRoleId: z.string().min(1),
  wxid: z.string().min(1),
});

export const scriptRoleReviewChangeSchema = z.object({
  tempRoleId: z.string().min(1),
  fromWxid: z.string().min(1),
  toWxid: z.string().min(1),
  changedAt: z.iso.datetime(),
});

export const confirmScriptRoleReviewInputSchema = z.object({
  reviewId: z.uuid(),
  spreadsheetToken: z.string().min(1),
  sheetId: z.string().min(1),
  sheetTitle: z.string().default(""),
  group: z.string().min(1),
  sourceFingerprint: z.string().min(1),
  overwriteMode: z.boolean().default(false),
  originalAssignmentSignature: z.string().min(1),
  imageTextsByFileToken: z
    .record(z.string().min(1), z.string().max(100_000))
    .default({}),
  reviewStartedAt: z.iso.datetime(),
  finalSelections: z.array(scriptRoleReviewSelectionSchema).min(1).max(500),
  changeHistory: z.array(scriptRoleReviewChangeSchema).max(2_000).default([]),
});

export type ConfirmScriptRoleReviewInput = z.infer<
  typeof confirmScriptRoleReviewInputSchema
>;
export type ScriptRoleReviewSelection = z.infer<
  typeof scriptRoleReviewSelectionSchema
>;
export type ScriptRoleReviewChange = z.infer<typeof scriptRoleReviewChangeSchema>;

export type ScriptRoleReviewMemberSnapshot = ScriptRoleMatchMemberSnapshot;

function hashSignatureSource(source: string) {
  let left = 0x811c9dc5;
  let right = 0x9e3779b9;

  for (let index = 0; index < source.length; index += 1) {
    const code = source.charCodeAt(index);
    left = Math.imul(left ^ code, 0x01000193);
    right = Math.imul(right ^ code, 0x85ebca6b);
  }

  return [left, right]
    .map((value) => (value >>> 0).toString(16).padStart(8, "0"))
    .join("");
}

/** Detects a stale review UI without making the client authoritative. */
export function buildRoleAssignmentReviewSignature(assignments: RoleAssignmentItem[]) {
  const normalized = [...assignments]
    .sort((left, right) => left.tempRoleId.localeCompare(right.tempRoleId, "zh-CN"))
    .map((item) => ({
      tempRoleId: item.tempRoleId,
      wxid: item.wxid,
      number: item.number,
      nickname: item.nickname,
      score: item.score,
      reason: item.reason,
      contentBeat: item.contentBeat,
      selectionSource: item.selectionSource,
      scoreBreakdown: item.scoreBreakdown,
    }));

  return `${SCRIPT_ROLE_REVIEW_SCHEMA_VERSION}:${hashSignatureSource(JSON.stringify(normalized))}`;
}

export function validateRoleReviewSelections(input: {
  tempRoleIds: string[];
  selections: ScriptRoleReviewSelection[];
  validMemberWxids: Iterable<string>;
}): { ok: true } | { ok: false; error: string } {
  const expectedRoleIds = new Set(input.tempRoleIds);
  if (expectedRoleIds.size !== input.tempRoleIds.length) {
    return { ok: false, error: "剧本包含重复的临时角色" };
  }

  const selectionByRoleId = new Map<string, string>();
  for (const selection of input.selections) {
    if (!expectedRoleIds.has(selection.tempRoleId)) {
      return { ok: false, error: `提交了不属于当前剧本的角色：${selection.tempRoleId}` };
    }
    if (selectionByRoleId.has(selection.tempRoleId)) {
      return { ok: false, error: `角色 ${selection.tempRoleId} 被重复提交` };
    }
    selectionByRoleId.set(selection.tempRoleId, selection.wxid);
  }

  const missingRoleIds = input.tempRoleIds.filter((id) => !selectionByRoleId.has(id));
  if (missingRoleIds.length > 0) {
    return { ok: false, error: `还有 ${missingRoleIds.length} 个角色未确认气氛员` };
  }

  const validMemberSet = new Set(input.validMemberWxids);
  const invalidSelection = input.selections.find(
    (selection) => !validMemberSet.has(selection.wxid)
  );
  if (invalidSelection) {
    return {
      ok: false,
      error: `角色 ${invalidSelection.tempRoleId} 选择的气氛员已不在当前分组`,
    };
  }

  return { ok: true };
}
